import { useState } from 'react';
import { Plus, RefreshCw, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { RcsConfigForm } from '@/components/rcs/RcsConfigForm';
import { RcsTemplateList } from '@/components/rcs/RcsTemplateList';
import { RcsTemplateFormDialog } from '@/components/rcs/RcsTemplateFormDialog';
import { RcsCampaignList } from '@/components/rcs/RcsCampaignList';
import { RcsCampaignFormDialog } from '@/components/rcs/RcsCampaignFormDialog';
import { RcsQuickSendDialog } from '@/components/rcs/RcsQuickSendDialog';
import { useSyncRcsTemplates } from '@/hooks/useRcs';
import type { RcsTemplate } from '@/types/api.types';

export function RcsSettingsPage() {
  const [templateFormOpen, setTemplateFormOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<RcsTemplate | null>(null);
  const [campaignFormOpen, setCampaignFormOpen] = useState(false);
  const [quickSendOpen, setQuickSendOpen] = useState(false);
  const syncMutation = useSyncRcsTemplates();

  function handleEditTemplate(template: RcsTemplate) {
    setEditTemplate(template);
    setTemplateFormOpen(true);
  }

  function handleCreateTemplate() {
    setEditTemplate(null);
    setTemplateFormOpen(true);
  }

  function handleTemplateFormOpenChange(open: boolean) {
    setTemplateFormOpen(open);
    if (!open) setEditTemplate(null);
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">RCS</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Integração RCS via Zenvia — configure o agente, gerencie templates e crie campanhas.
          </p>
        </div>
        <Button variant="outline" onClick={() => setQuickSendOpen(true)}>
          <Send className="h-4 w-4" />
          Envio rápido
        </Button>
      </div>

      <RcsConfigForm />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Templates RCS</h2>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
            >
              <RefreshCw className={`h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              Sincronizar da Zenvia
            </Button>
            <Button onClick={handleCreateTemplate}>
              <Plus className="h-4 w-4" />
              Novo template
            </Button>
          </div>
        </div>
        <Card>
          <CardContent className="pt-6">
            <RcsTemplateList onEdit={handleEditTemplate} />
          </CardContent>
        </Card>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Campanhas RCS</h2>
          <Button onClick={() => setCampaignFormOpen(true)}>
            <Plus className="h-4 w-4" />
            Nova campanha
          </Button>
        </div>
        <RcsCampaignList />
      </div>

      <RcsTemplateFormDialog
        open={templateFormOpen}
        onOpenChange={handleTemplateFormOpenChange}
        template={editTemplate}
      />

      <RcsCampaignFormDialog open={campaignFormOpen} onOpenChange={setCampaignFormOpen} />

      <RcsQuickSendDialog open={quickSendOpen} onOpenChange={setQuickSendOpen} />
    </div>
  );
}
